import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// PRE-LAUNCH LOCK-DOWN — the multilingual rollout was postponed (see PRD i165).
// Wipe any previously-stored language preference so users who switched to
// (e.g.) `zh` or `ja` during the brief window the switcher was live don't get
// stuck with the navbar partially in that language now that the rest of the
// site (HomePage hero, etc.) is still hardcoded English. When we ship the
// proper i18n refactor post-launch, delete this block.
if (typeof window !== "undefined") {
  try {
    // i18next-browser-languagedetector stores the picked lang under
    // `i18nextLng` by default. Our LanguageSwitcher used the same key.
    window.localStorage.removeItem("i18nextLng");
    window.localStorage.removeItem("bullpugLang");
  } catch (_) {
    // Storage unavailable (sandboxed iframe / private mode) — no-op.
  }
}

export const supportedLanguages = {
  en: { nativeName: 'English', flag: '🇺🇸' },
  es: { nativeName: 'Español', flag: '🇪🇸' },
  zh: { nativeName: '中文', flag: '🇨🇳' },
  ja: { nativeName: '日本語', flag: '🇯🇵' },
  ko: { nativeName: '한국어', flag: '🇰🇷' },
  de: { nativeName: 'Deutsch', flag: '🇩🇪' },
  fr: { nativeName: 'Français', flag: '🇫🇷' },
  pt: { nativeName: 'Português', flag: '🇧🇷' },
  ru: { nativeName: 'Русский', flag: '🇷🇺' },
  ar: { nativeName: 'العربية', flag: '🇸🇦' }
};

// English translations
const enTranslations = {
  // Common
  common: {
    loading: 'Loading...',
    error: 'An error occurred',
    success: 'Success',
    cancel: 'Cancel',
    submit: 'Submit',
    save: 'Save',
    delete: 'Delete',
    back: 'Back',
    refresh: 'Refresh',
    connectWallet: 'Connect Wallet',
    walletRequired: 'Wallet Required',
    language: 'Language'
  },
  // Navbar
  nav: {
    home: 'Home',
    lore: 'Origins',
    arena: 'Pug Pit',
    game: 'Cosmic Runner',
    exitSim: 'Exit Sim',
    reflections: 'Reflections',
    journal: 'Journal',
    forum: 'Forum',
    wallet: 'Wallet',
    admin: 'Admin',
    messages: 'Messages'
  },
  // Betting Arena
  betting: {
    title: 'Pug Pit',
    subtitle: 'Player vs Player • Real SOL • {{rake}}% Rake',
    solOnly: 'SOL Only',
    disclaimer: 'For entertainment - check local laws',
    connectWalletToPlay: 'Connect your Solana wallet to play',
    p2pRequiresWallet: 'P2P betting requires a connected wallet with SOL',
    // Coin Flip
    coinFlip: {
      title: 'P2P Coin Flip',
      createChallenge: 'Create Challenge',
      yourName: 'Your Name',
      betAmount: 'Bet Amount (SOL)',
      yourPick: 'Your Pick',
      heads: 'Heads',
      tails: 'Tails',
      opponentBet: 'Opponent Bet',
      totalPot: 'Total Pot',
      rake: 'Rake',
      winnerGets: 'Winner Gets',
      creating: 'Creating...',
      createBtn: 'Create Challenge',
      yourOpenChallenges: 'Your Open Challenges',
      waitingForOpponent: 'Waiting for opponent...',
      cancel: 'Cancel',
      openChallenges: 'Open Challenges',
      noChallenges: 'No open challenges',
      createOrWait: 'Create one or wait for others!',
      picked: 'Picked',
      youPick: 'You pick',
      accept: 'Accept',
      flipping: 'Flipping...',
      recentFlips: 'Recent P2P Flips',
      youWon: 'YOU WON {{amount}} SOL!',
      youLost: 'YOU LOST',
      coinLandedOn: 'Coin landed on',
      provablyFair: 'Provably Fair Verification',
      serverSeed: 'Server Seed',
      clientSeed: 'Client Seed',
      resultHash: 'Result Hash'
    },
    // Pot
    pot: {
      title: 'Winner Pot',
      joinPot: 'Join Pot',
      yourName: 'Your Name',
      betAmount: 'Bet Amount (SOL)',
      higherBetHigherChance: 'Higher bet = higher win probability. Winner takes all minus {{rake}}% rake.',
      joining: 'Joining...',
      joinBtn: 'Join Pot',
      rakeGoesTo: 'Rake goes to',
      currentPot: 'Current Pot',
      entries: 'Entries',
      won: '{{name}} won {{amount}} SOL!',
      participants: 'Participants',
      chance: 'chance',
      noEntries: 'No entries yet. Be the first!',
      howItWorks: 'How P2P Pot Works',
      rule1: 'Multiple players contribute SOL to the pot',
      rule2: 'Your win probability = your bet / total pot',
      rule3: 'When drawn, one winner takes all',
      rule4: 'Provably fair random selection'
    }
  },
  // Speed Run Game
  game: {
    title: 'Speed Run',
    subtitle: 'Dodge obstacles, collect Moon Cheese, activate power-ups',
    resetsIn: 'Resets in {{days}}d',
    startGame: 'Start Game',
    spaceToJump: 'SPACE / Tap to jump (double jump!)',
    gameOver: 'GAME OVER',
    score: 'Score',
    moonCheese: 'Moon Cheese',
    newHighScore: 'New High Score!',
    playAgain: 'Play Again',
    shareOnX: 'Share on',
    shareText: 'I just scored {{score}} points in the Bullpug Speed Run game! Can you beat my score?',
    highScore: 'High Score',
    totalMoonCheese: 'Total Moon Cheese',
    controls: 'Controls',
    weeklyLeaderboard: 'Leaderboard',
    noScoresYet: 'No scores yet. Be the first!',
    resetMonday: '',
    moonCheeses: 'moon cheese',
    // Power-ups
    shield: 'Shield',
    magnet: 'Magnet',
    doubleScore: '2x Score'
  }
};

// Spanish translations
const esTranslations = {
  // Common
  common: {
    loading: 'Cargando...',
    error: 'Ocurrió un error',
    success: 'Éxito',
    cancel: 'Cancelar',
    submit: 'Enviar',
    save: 'Guardar',
    delete: 'Eliminar',
    back: 'Atrás',
    refresh: 'Actualizar',
    connectWallet: 'Conectar Billetera',
    walletRequired: 'Billetera Requerida',
    language: 'Idioma'
  },
  // Navbar
  nav: {
    home: 'Inicio',
    lore: 'Historia',
    arena: 'Pug Pit',
    game: 'Juego',
    exitSim: 'Simulador',
    reflections: 'Reflexiones',
    journal: 'Diario',
    forum: 'Foro',
    wallet: 'Billetera',
    admin: 'Admin',
    messages: 'Mensajes'
  },
  // Betting Arena
  betting: {
    title: 'Arena P2P',
    subtitle: 'Jugador vs Jugador • SOL Real • {{rake}}% Comisión',
    solOnly: 'Solo SOL',
    disclaimer: 'Para entretenimiento - verifica las leyes locales',
    connectWalletToPlay: 'Conecta tu billetera Solana para jugar',
    p2pRequiresWallet: 'Las apuestas P2P requieren una billetera conectada con SOL',
    // Coin Flip
    coinFlip: {
      title: 'Volado P2P',
      createChallenge: 'Crear Desafío',
      yourName: 'Tu Nombre',
      betAmount: 'Cantidad (SOL)',
      yourPick: 'Tu Elección',
      heads: 'Cara',
      tails: 'Cruz',
      opponentBet: 'Apuesta Oponente',
      totalPot: 'Pozo Total',
      rake: 'Comisión',
      winnerGets: 'Ganador Recibe',
      creating: 'Creando...',
      createBtn: 'Crear Desafío',
      yourOpenChallenges: 'Tus Desafíos Abiertos',
      waitingForOpponent: 'Esperando oponente...',
      cancel: 'Cancelar',
      openChallenges: 'Desafíos Abiertos',
      noChallenges: 'No hay desafíos abiertos',
      createOrWait: '¡Crea uno o espera a otros!',
      picked: 'Eligió',
      youPick: 'Tú eliges',
      accept: 'Aceptar',
      flipping: 'Lanzando...',
      recentFlips: 'Volados P2P Recientes',
      youWon: '¡GANASTE {{amount}} SOL!',
      youLost: 'PERDISTE',
      coinLandedOn: 'La moneda cayó en',
      provablyFair: 'Verificación Justa',
      serverSeed: 'Semilla Servidor',
      clientSeed: 'Semilla Cliente',
      resultHash: 'Hash Resultado'
    },
    // Pot
    pot: {
      title: 'Pozo de Premios',
      joinPot: 'Unirse al Pozo',
      yourName: 'Tu Nombre',
      betAmount: 'Cantidad (SOL)',
      higherBetHigherChance: 'Mayor apuesta = mayor probabilidad de ganar. El ganador se lleva todo menos {{rake}}% de comisión.',
      joining: 'Uniéndose...',
      joinBtn: 'Unirse al Pozo',
      rakeGoesTo: 'Comisión va a',
      currentPot: 'Pozo Actual',
      entries: 'Entradas',
      won: '¡{{name}} ganó {{amount}} SOL!',
      participants: 'Participantes',
      chance: 'probabilidad',
      noEntries: 'Sin entradas aún. ¡Sé el primero!',
      howItWorks: 'Cómo Funciona el Pozo P2P',
      rule1: 'Múltiples jugadores contribuyen SOL al pozo',
      rule2: 'Tu probabilidad de ganar = tu apuesta / pozo total',
      rule3: 'Al sortear, un ganador se lleva todo',
      rule4: 'Selección aleatoria verificable'
    }
  },
  // Speed Run Game
  game: {
    title: 'Carrera',
    subtitle: 'Esquiva obstáculos, colecta Moon Cheese, activa power-ups',
    resetsIn: 'Reinicia en {{days}}d',
    startGame: 'Iniciar Juego',
    spaceToJump: 'ESPACIO / Toca para saltar (¡doble salto!)',
    gameOver: 'FIN DEL JUEGO',
    score: 'Puntaje',
    moonCheese: 'Moon Cheese',
    newHighScore: '¡Nuevo Récord!',
    playAgain: 'Jugar de Nuevo',
    shareOnX: 'Compartir en',
    shareText: '¡Acabo de obtener {{score}} puntos en el juego Speed Run de Bullpug! ¿Puedes superar mi puntaje?',
    highScore: 'Récord',
    totalMoonCheeseCount: 'Moon Cheese Total',
    controls: 'Controles',
    weeklyLeaderboard: 'Tabla de Líderes',
    noScoresYet: '¡Sin puntajes aún. Sé el primero!',
    resetMonday: '',
    moonCheeses: 'moon cheese',
    // Power-ups
    shield: 'Escudo',
    magnet: 'Imán',
    doubleScore: '2x Puntos'
  }
};

const zhTranslations = {
  "common": {
    "loading": "加载中...",
    "error": "发生错误",
    "success": "成功",
    "cancel": "取消",
    "submit": "提交",
    "save": "保存",
    "delete": "删除",
    "back": "返回",
    "refresh": "刷新",
    "connectWallet": "连接钱包",
    "walletRequired": "需要钱包",
    "language": "语言"
  },
  "nav": {
    "home": "首页",
    "lore": "起源",
    "arena": "P2P 竞技场",
    "game": "宇宙跑酷",
    "exitSim": "退出模拟",
    "reflections": "反思",
    "journal": "日志",
    "forum": "论坛",
    "wallet": "钱包",
    "admin": "管理",
    "messages": "消息"
  },
  "betting": {
    "title": "P2P 竞技场",
    "subtitle": "玩家对战 • 真实 SOL • {{rake}}% 抽成",
    "solOnly": "仅限 SOL",
    "disclaimer": "仅供娱乐 - 请遵守当地法律",
    "connectWalletToPlay": "连接 Solana 钱包开始游戏",
    "p2pRequiresWallet": "P2P 下注需要连接有 SOL 的钱包",
    "coinFlip": {
      "title": "P2P 抛硬币",
      "createChallenge": "创建挑战",
      "yourName": "你的名字",
      "betAmount": "下注金额 (SOL)",
      "yourPick": "你的选择",
      "heads": "正面",
      "tails": "反面",
      "opponentBet": "对手下注",
      "totalPot": "总奖池",
      "rake": "抽成",
      "winnerGets": "赢家获得",
      "creating": "创建中...",
      "createBtn": "创建挑战",
      "yourOpenChallenges": "你的待接挑战",
      "waitingForOpponent": "等待对手...",
      "cancel": "取消",
      "openChallenges": "待接挑战",
      "noChallenges": "暂无挑战",
      "createOrWait": "创建一个或等待他人!",
      "picked": "选了",
      "youPick": "你选",
      "accept": "接受",
      "flipping": "抛币中...",
      "recentFlips": "最近 P2P 对战",
      "youWon": "你赢了 {{amount}} SOL!",
      "youLost": "你输了",
      "coinLandedOn": "硬币落在",
      "provablyFair": "可证公平验证",
      "serverSeed": "服务器种子",
      "clientSeed": "客户端种子",
      "resultHash": "结果哈希"
    },
    "pot": {
      "title": "赢家奖池",
      "joinPot": "加入奖池",
      "yourName": "你的名字",
      "betAmount": "下注金额 (SOL)",
      "higherBetHigherChance": "下注越多获胜概率越高。赢家获得全部减去 {{rake}}% 抽成。",
      "joining": "加入中...",
      "joinBtn": "加入奖池",
      "rakeGoesTo": "抽成归于",
      "currentPot": "当前奖池",
      "entries": "参与者",
      "won": "{{name}} 赢得 {{amount}} SOL!",
      "participants": "参与者",
      "chance": "胜率",
      "noEntries": "暂无参与者。成为第一个!",
      "howItWorks": "P2P 奖池规则",
      "rule1": "多名玩家向奖池投入 SOL",
      "rule2": "你的胜率 = 你的投入 / 总奖池",
      "rule3": "开奖时一名赢家获得全部",
      "rule4": "可证公平随机抽取"
    }
  },
  "game": {
    "title": "极速跑酷",
    "subtitle": "躲避障碍,收集月亮奶酪,激活能量道具",
    "resetsIn": "{{days}}天后重置",
    "startGame": "开始游戏",
    "spaceToJump": "空格 / 点击跳跃(可二段跳!)",
    "gameOver": "游戏结束",
    "score": "得分",
    "moonCheese": "月亮奶酪",
    "newHighScore": "新纪录!",
    "playAgain": "再玩一次",
    "shareOnX": "分享到",
    "shareText": "我在 Bullpug 极速跑酷中得了 {{score}} 分!你能超越我吗?",
    "highScore": "最高分",
    "totalMoonCheese": "总月亮奶酪",
    "controls": "操作",
    "weeklyLeaderboard": "排行榜",
    "noScoresYet": "暂无记录。成为第一个!",
    "resetMonday": "",
    "moonCheeses": "月亮奶酪",
    "shield": "护盾",
    "magnet": "磁铁",
    "doubleScore": "2倍得分"
  }
};

const jaTranslations = {
  "common": {
    "loading": "読み込み中...",
    "error": "エラーが発生しました",
    "success": "成功",
    "cancel": "キャンセル",
    "submit": "送信",
    "save": "保存",
    "delete": "削除",
    "back": "戻る",
    "refresh": "更新",
    "connectWallet": "ウォレット接続",
    "walletRequired": "ウォレットが必要",
    "language": "言語"
  },
  "nav": {
    "home": "ホーム",
    "lore": "起源",
    "arena": "Pug Pit",
    "game": "コズミックランナー",
    "exitSim": "シム終了",
    "reflections": "リフレクション",
    "journal": "ジャーナル",
    "forum": "フォーラム",
    "wallet": "ウォレット",
    "admin": "管理",
    "messages": "メッセージ"
  },
  "betting": {
    "title": "Pug Pit",
    "subtitle": "対戦 • リアルSOL • レーキ{{rake}}%",
    "solOnly": "SOLのみ",
    "disclaimer": "娯楽用 - 現地法律を確認してください",
    "connectWalletToPlay": "プレイするにはSolanaウォレットを接続",
    "p2pRequiresWallet": "P2P対戦にはSOL入りウォレットが必要",
    "coinFlip": {
      "title": "P2Pコインフリップ",
      "createChallenge": "チャレンジ作成",
      "yourName": "名前",
      "betAmount": "ベット額(SOL)",
      "yourPick": "選択",
      "heads": "表",
      "tails": "裏",
      "opponentBet": "対戦相手ベット",
      "totalPot": "総額",
      "rake": "レーキ",
      "winnerGets": "勝者獲得額",
      "creating": "作成中...",
      "createBtn": "チャレンジ作成",
      "yourOpenChallenges": "あなたのチャレンジ",
      "waitingForOpponent": "対戦相手を待機中...",
      "cancel": "キャンセル",
      "openChallenges": "オープンチャレンジ",
      "noChallenges": "チャレンジなし",
      "createOrWait": "作成するか他の人を待とう!",
      "picked": "選択",
      "youPick": "あなたの選択",
      "accept": "受ける",
      "flipping": "フリップ中...",
      "recentFlips": "最近のP2Pフリップ",
      "youWon": "勝利 {{amount}} SOL!",
      "youLost": "敗北",
      "coinLandedOn": "結果",
      "provablyFair": "証明可能な公平性検証",
      "serverSeed": "サーバーシード",
      "clientSeed": "クライアントシード",
      "resultHash": "結果ハッシュ"
    },
    "pot": {
      "title": "勝者ポット",
      "joinPot": "ポット参加",
      "yourName": "名前",
      "betAmount": "ベット額(SOL)",
      "higherBetHigherChance": "ベット額が高いほど勝率アップ。勝者が総取り(レーキ{{rake}}%)",
      "joining": "参加中...",
      "joinBtn": "ポット参加",
      "rakeGoesTo": "レーキ送金先",
      "currentPot": "現在のポット",
      "entries": "エントリー",
      "won": "{{name}}が{{amount}} SOL獲得!",
      "participants": "参加者",
      "chance": "勝率",
      "noEntries": "まだエントリーなし。最初になろう!",
      "howItWorks": "P2Pポットの仕組み",
      "rule1": "複数プレイヤーがSOLをポットに投入",
      "rule2": "勝率 = 自分のベット / ポット総額",
      "rule3": "抽選時、1人の勝者が総取り",
      "rule4": "証明可能な公平性ランダム選出"
    }
  },
  "game": {
    "title": "スピードラン",
    "subtitle": "障害物を避けてMoon Cheese集め、パワーアップ起動",
    "resetsIn": "リセット{{days}}日後",
    "startGame": "ゲーム開始",
    "spaceToJump": "スペース/タップでジャンプ(二段ジャンプ可)",
    "gameOver": "ゲームオーバー",
    "score": "スコア",
    "moonCheese": "Moon Cheese",
    "newHighScore": "ハイスコア更新!",
    "playAgain": "もう一度",
    "shareOnX": "共有",
    "shareText": "Bullpugスピードランで{{score}}点獲得!君は超えられる?",
    "highScore": "ハイスコア",
    "totalMoonCheese": "合計Moon Cheese",
    "controls": "操作",
    "weeklyLeaderboard": "ランキング",
    "noScoresYet": "まだ記録なし。最初になろう!",
    "resetMonday": "",
    "moonCheeses": "moon cheese",
    "shield": "シールド",
    "magnet": "マグネット",
    "doubleScore": "2倍スコア"
  }
};

const koTranslations = {
  "common": {
    "loading": "로딩 중...",
    "error": "오류가 발생했습니다",
    "success": "성공",
    "cancel": "취소",
    "submit": "제출",
    "save": "저장",
    "delete": "삭제",
    "back": "뒤로",
    "refresh": "새로고침",
    "connectWallet": "지갑 연결",
    "walletRequired": "지갑 필요",
    "language": "언어"
  },
  "nav": {
    "home": "홈",
    "lore": "기원",
    "arena": "P2P 아레나",
    "game": "코스믹 러너",
    "exitSim": "시뮬레이션 종료",
    "reflections": "성찰",
    "journal": "일지",
    "forum": "포럼",
    "wallet": "지갑",
    "admin": "관리자",
    "messages": "메시지"
  },
  "betting": {
    "title": "P2P 아레나",
    "subtitle": "플레이어 대 플레이어 • 실제 SOL • {{rake}}% 수수료",
    "solOnly": "SOL만 가능",
    "disclaimer": "오락용 - 현지 법률 확인 필요",
    "connectWalletToPlay": "플레이하려면 Solana 지갑을 연결하세요",
    "p2pRequiresWallet": "P2P 베팅은 SOL이 있는 지갑 연결이 필요합니다",
    "coinFlip": {
      "title": "P2P 동전 뒤집기",
      "createChallenge": "도전 생성",
      "yourName": "이름",
      "betAmount": "베팅 금액 (SOL)",
      "yourPick": "선택",
      "heads": "앞면",
      "tails": "뒷면",
      "opponentBet": "상대 베팅",
      "totalPot": "총 팟",
      "rake": "수수료",
      "winnerGets": "승자 획득",
      "creating": "생성 중...",
      "createBtn": "도전 생성",
      "yourOpenChallenges": "내 열린 도전",
      "waitingForOpponent": "상대 대기 중...",
      "cancel": "취소",
      "openChallenges": "열린 도전",
      "noChallenges": "열린 도전이 없습니다",
      "createOrWait": "도전을 만들거나 다른 사람을 기다리세요!",
      "picked": "선택함",
      "youPick": "당신 선택",
      "accept": "수락",
      "flipping": "뒤집는 중...",
      "recentFlips": "최근 P2P 뒤집기",
      "youWon": "{{amount}} SOL 획득!",
      "youLost": "패배",
      "coinLandedOn": "동전 결과",
      "provablyFair": "공정성 검증",
      "serverSeed": "서버 시드",
      "clientSeed": "클라이언트 시드",
      "resultHash": "결과 해시"
    },
    "pot": {
      "title": "승자 팟",
      "joinPot": "팟 참여",
      "yourName": "이름",
      "betAmount": "베팅 금액 (SOL)",
      "higherBetHigherChance": "높은 베팅 = 높은 승률. 승자가 전부를 가져가며 {{rake}}% 수수료 제외.",
      "joining": "참여 중...",
      "joinBtn": "팟 참여",
      "rakeGoesTo": "수수료 귀속",
      "currentPot": "현재 팟",
      "entries": "참가자",
      "won": "{{name}}님이 {{amount}} SOL 획득!",
      "participants": "참가자",
      "chance": "확률",
      "noEntries": "아직 참가자가 없습니다. 첫 번째가 되세요!",
      "howItWorks": "P2P 팟 방식",
      "rule1": "여러 플레이어가 팟에 SOL 기여",
      "rule2": "승률 = 내 베팅 / 총 팟",
      "rule3": "추첨 시 한 명의 승자가 전부 획득",
      "rule4": "공정성 보장 랜덤 선택"
    }
  },
  "game": {
    "title": "스피드 런",
    "subtitle": "장애물 피하기, Moon Cheese 수집, 파워업 활성화",
    "resetsIn": "{{days}}일 후 리셋",
    "startGame": "게임 시작",
    "spaceToJump": "스페이스 / 탭으로 점프 (더블 점프!)",
    "gameOver": "게임 오버",
    "score": "점수",
    "moonCheese": "Moon Cheese",
    "newHighScore": "신기록!",
    "playAgain": "다시 플레이",
    "shareOnX": "공유",
    "shareText": "Bullpug 스피드 런에서 {{score}}점 달성! 내 점수를 넘어볼래?",
    "highScore": "최고 점수",
    "totalMoonCheese": "총 Moon Cheese",
    "controls": "조작",
    "weeklyLeaderboard": "순위표",
    "noScoresYet": "아직 점수가 없습니다. 첫 번째가 되세요!",
    "resetMonday": "",
    "moonCheeses": "moon cheese",
    "shield": "보호막",
    "magnet": "자석",
    "doubleScore": "2배 점수"
  }
};

const deTranslations = {
  "common": {
    "loading": "Lädt...",
    "error": "Ein Fehler ist aufgetreten",
    "success": "Erfolg",
    "cancel": "Abbrechen",
    "submit": "Senden",
    "save": "Speichern",
    "delete": "Löschen",
    "back": "Zurück",
    "refresh": "Aktualisieren",
    "connectWallet": "Wallet verbinden",
    "walletRequired": "Wallet erforderlich",
    "language": "Sprache"
  },
  "nav": {
    "home": "Start",
    "lore": "Ursprünge",
    "arena": "Pug Pit",
    "game": "Cosmic Runner",
    "exitSim": "Sim verlassen",
    "reflections": "Reflexionen",
    "journal": "Journal",
    "forum": "Forum",
    "wallet": "Wallet",
    "admin": "Admin",
    "messages": "Nachrichten"
  },
  "betting": {
    "title": "Pug Pit",
    "subtitle": "Spieler vs Spieler • Echte SOL • {{rake}}% Rake",
    "solOnly": "Nur SOL",
    "disclaimer": "Zur Unterhaltung - prüfe lokale Gesetze",
    "connectWalletToPlay": "Verbinde deine Solana Wallet zum Spielen",
    "p2pRequiresWallet": "P2P Wetten erfordern eine verbundene Wallet mit SOL",
    "coinFlip": {
      "title": "P2P Münzwurf",
      "createChallenge": "Herausforderung erstellen",
      "yourName": "Dein Name",
      "betAmount": "Wetteinsatz (SOL)",
      "yourPick": "Deine Wahl",
      "heads": "Kopf",
      "tails": "Zahl",
      "opponentBet": "Gegnereinsatz",
      "totalPot": "Gesamtpott",
      "rake": "Rake",
      "winnerGets": "Gewinner erhält",
      "creating": "Erstelle...",
      "createBtn": "Herausforderung erstellen",
      "yourOpenChallenges": "Deine offenen Herausforderungen",
      "waitingForOpponent": "Warte auf Gegner...",
      "cancel": "Abbrechen",
      "openChallenges": "Offene Herausforderungen",
      "noChallenges": "Keine offenen Herausforderungen",
      "createOrWait": "Erstelle eine oder warte auf andere!",
      "picked": "Gewählt",
      "youPick": "Du wählst",
      "accept": "Annehmen",
      "flipping": "Wirft...",
      "recentFlips": "Letzte P2P Würfe",
      "youWon": "DU HAST {{amount}} SOL GEWONNEN!",
      "youLost": "DU HAST VERLOREN",
      "coinLandedOn": "Münze landete auf",
      "provablyFair": "Nachweislich faire Überprüfung",
      "serverSeed": "Server-Seed",
      "clientSeed": "Client-Seed",
      "resultHash": "Ergebnis-Hash"
    },
    "pot": {
      "title": "Gewinnpott",
      "joinPot": "Pott beitreten",
      "yourName": "Dein Name",
      "betAmount": "Wetteinsatz (SOL)",
      "higherBetHigherChance": "Höherer Einsatz = höhere Gewinnwahrscheinlichkeit. Gewinner erhält alles minus {{rake}}% Rake.",
      "joining": "Trete bei...",
      "joinBtn": "Pott beitreten",
      "rakeGoesTo": "Rake geht an",
      "currentPot": "Aktueller Pott",
      "entries": "Einträge",
      "won": "{{name}} hat {{amount}} SOL gewonnen!",
      "participants": "Teilnehmer",
      "chance": "Chance",
      "noEntries": "Noch keine Einträge. Sei der Erste!",
      "howItWorks": "So funktioniert P2P Pott",
      "rule1": "Mehrere Spieler zahlen SOL in den Pott ein",
      "rule2": "Deine Gewinnwahrscheinlichkeit = dein Einsatz / Gesamtpott",
      "rule3": "Bei der Ziehung gewinnt ein Spieler alles",
      "rule4": "Nachweislich faire Zufallsauswahl"
    }
  },
  "game": {
    "title": "Speed Run",
    "subtitle": "Weiche Hindernissen aus, sammle Moon Cheese, aktiviere Power-ups",
    "resetsIn": "Reset in {{days}}T",
    "startGame": "Spiel starten",
    "spaceToJump": "LEERTASTE / Tippen zum Springen (Doppelsprung!)",
    "gameOver": "GAME OVER",
    "score": "Punkte",
    "moonCheese": "Moon Cheese",
    "newHighScore": "Neuer Highscore!",
    "playAgain": "Nochmal spielen",
    "shareOnX": "Teilen auf",
    "shareText": "Ich habe {{score}} Punkte im Bullpug Speed Run Spiel erreicht! Kannst du meinen Score schlagen?",
    "highScore": "Highscore",
    "totalMoonCheese": "Gesamt Moon Cheese",
    "controls": "Steuerung",
    "weeklyLeaderboard": "Bestenliste",
    "noScoresYet": "Noch keine Scores. Sei der Erste!",
    "resetMonday": "",
    "moonCheeses": "Moon Cheese",
    "shield": "Schild",
    "magnet": "Magnet",
    "doubleScore": "2x Punkte"
  }
};

const frTranslations = {
  "common": {
    "loading": "Chargement...",
    "error": "Une erreur est survenue",
    "success": "Succès",
    "cancel": "Annuler",
    "submit": "Envoyer",
    "save": "Sauvegarder",
    "delete": "Supprimer",
    "back": "Retour",
    "refresh": "Actualiser",
    "connectWallet": "Connecter",
    "walletRequired": "Portefeuille requis",
    "language": "Langue"
  },
  "nav": {
    "home": "Accueil",
    "lore": "Origines",
    "arena": "Arène P2P",
    "game": "Cosmic Runner",
    "exitSim": "Quitter Sim",
    "reflections": "Réflexions",
    "journal": "Journal",
    "forum": "Forum",
    "wallet": "Portefeuille",
    "admin": "Admin",
    "messages": "Messages"
  },
  "betting": {
    "title": "Arène P2P",
    "subtitle": "Joueur vs Joueur • SOL réel • {{rake}}% de frais",
    "solOnly": "SOL uniquement",
    "disclaimer": "Divertissement - vérifiez lois locales",
    "connectWalletToPlay": "Connectez votre portefeuille Solana pour jouer",
    "p2pRequiresWallet": "P2P nécessite un portefeuille connecté avec SOL",
    "coinFlip": {
      "title": "Pile ou Face P2P",
      "createChallenge": "Créer défi",
      "yourName": "Votre nom",
      "betAmount": "Mise (SOL)",
      "yourPick": "Votre choix",
      "heads": "Heads",
      "tails": "Tails",
      "opponentBet": "Mise adversaire",
      "totalPot": "Cagnotte totale",
      "rake": "Frais",
      "winnerGets": "Le gagnant reçoit",
      "creating": "Création...",
      "createBtn": "Créer défi",
      "yourOpenChallenges": "Vos défis ouverts",
      "waitingForOpponent": "En attente d'adversaire...",
      "cancel": "Annuler",
      "openChallenges": "Défis ouverts",
      "noChallenges": "Aucun défi ouvert",
      "createOrWait": "Créez-en un ou attendez!",
      "picked": "A choisi",
      "youPick": "Vous choisissez",
      "accept": "Accepter",
      "flipping": "Lancement...",
      "recentFlips": "Lancers P2P récents",
      "youWon": "VOUS AVEZ GAGNÉ {{amount}} SOL!",
      "youLost": "VOUS AVEZ PERDU",
      "coinLandedOn": "La pièce est tombée sur",
      "provablyFair": "Vérification équitable prouvable",
      "serverSeed": "Graine serveur",
      "clientSeed": "Graine client",
      "resultHash": "Hash résultat"
    },
    "pot": {
      "title": "Cagnotte gagnante",
      "joinPot": "Rejoindre",
      "yourName": "Votre nom",
      "betAmount": "Mise (SOL)",
      "higherBetHigherChance": "Mise plus élevée = probabilité plus élevée. Le gagnant remporte tout moins {{rake}}% de frais.",
      "joining": "Participation...",
      "joinBtn": "Rejoindre",
      "rakeGoesTo": "Les frais vont à",
      "currentPot": "Cagnotte actuelle",
      "entries": "Entrées",
      "won": "{{name}} a gagné {{amount}} SOL!",
      "participants": "Participants",
      "chance": "chance",
      "noEntries": "Aucune entrée. Soyez le premier!",
      "howItWorks": "Comment fonctionne la cagnotte P2P",
      "rule1": "Plusieurs joueurs contribuent SOL à la cagnotte",
      "rule2": "Votre probabilité = votre mise / cagnotte totale",
      "rule3": "Au tirage, un gagnant remporte tout",
      "rule4": "Sélection aléatoire équitable prouvable"
    }
  },
  "game": {
    "title": "Speed Run",
    "subtitle": "Évitez obstacles, collectez Moon Cheese, activez bonus",
    "resetsIn": "Réinit dans {{days}}j",
    "startGame": "Démarrer",
    "spaceToJump": "ESPACE / Appui pour sauter (double saut!)",
    "gameOver": "GAME OVER",
    "score": "Score",
    "moonCheese": "Moon Cheese",
    "newHighScore": "Nouveau record!",
    "playAgain": "Rejouer",
    "shareOnX": "Partager sur",
    "shareText": "J'ai marqué {{score}} points dans Bullpug Speed Run! Pouvez-vous battre mon score?",
    "highScore": "Record",
    "totalMoonCheese": "Total Moon Cheese",
    "controls": "Contrôles",
    "weeklyLeaderboard": "Classement",
    "noScoresYet": "Aucun score. Soyez le premier!",
    "resetMonday": "",
    "moonCheeses": "moon cheese",
    "shield": "Bouclier",
    "magnet": "Aimant",
    "doubleScore": "Score x2"
  }
};

const ptTranslations = {
  "common": {
    "loading": "Carregando...",
    "error": "Ocorreu um erro",
    "success": "Sucesso",
    "cancel": "Cancelar",
    "submit": "Enviar",
    "save": "Salvar",
    "delete": "Excluir",
    "back": "Voltar",
    "refresh": "Atualizar",
    "connectWallet": "Conectar Carteira",
    "walletRequired": "Carteira Necessária",
    "language": "Idioma"
  },
  "nav": {
    "home": "Início",
    "lore": "Origens",
    "arena": "Arena P2P",
    "game": "Corredor Cósmico",
    "exitSim": "Sair da Simulação",
    "reflections": "Reflexões",
    "journal": "Diário",
    "forum": "Fórum",
    "wallet": "Carteira",
    "admin": "Admin",
    "messages": "Mensagens"
  },
  "betting": {
    "title": "Arena P2P",
    "subtitle": "Jogador vs Jogador • SOL Real • {{rake}}% Taxa",
    "solOnly": "Apenas SOL",
    "disclaimer": "Para entretenimento - verifique leis locais",
    "connectWalletToPlay": "Conecte sua carteira Solana para jogar",
    "p2pRequiresWallet": "Apostas P2P requerem carteira conectada com SOL",
    "coinFlip": {
      "title": "Cara ou Coroa P2P",
      "createChallenge": "Criar Desafio",
      "yourName": "Seu Nome",
      "betAmount": "Valor da Aposta (SOL)",
      "yourPick": "Sua Escolha",
      "heads": "Cara",
      "tails": "Coroa",
      "opponentBet": "Aposta do Oponente",
      "totalPot": "Pote Total",
      "rake": "Taxa",
      "winnerGets": "Vencedor Ganha",
      "creating": "Criando...",
      "createBtn": "Criar Desafio",
      "yourOpenChallenges": "Seus Desafios Abertos",
      "waitingForOpponent": "Aguardando oponente...",
      "cancel": "Cancelar",
      "openChallenges": "Desafios Abertos",
      "noChallenges": "Nenhum desafio aberto",
      "createOrWait": "Crie um ou aguarde outros!",
      "picked": "Escolheu",
      "youPick": "Você escolhe",
      "accept": "Aceitar",
      "flipping": "Girando...",
      "recentFlips": "Jogadas P2P Recentes",
      "youWon": "VOCÊ GANHOU {{amount}} SOL!",
      "youLost": "VOCÊ PERDEU",
      "coinLandedOn": "Moeda caiu em",
      "provablyFair": "Verificação Comprovadamente Justa",
      "serverSeed": "Semente do Servidor",
      "clientSeed": "Semente do Cliente",
      "resultHash": "Hash do Resultado"
    },
    "pot": {
      "title": "Pote do Vencedor",
      "joinPot": "Entrar no Pote",
      "yourName": "Seu Nome",
      "betAmount": "Valor da Aposta (SOL)",
      "higherBetHigherChance": "Aposta maior = maior probabilidade. Vencedor leva tudo menos {{rake}}% de taxa.",
      "joining": "Entrando...",
      "joinBtn": "Entrar no Pote",
      "rakeGoesTo": "Taxa vai para",
      "currentPot": "Pote Atual",
      "entries": "Entradas",
      "won": "{{name}} ganhou {{amount}} SOL!",
      "participants": "Participantes",
      "chance": "chance",
      "noEntries": "Sem entradas ainda. Seja o primeiro!",
      "howItWorks": "Como Funciona o Pote P2P",
      "rule1": "Vários jogadores contribuem SOL para o pote",
      "rule2": "Sua probabilidade = sua aposta / pote total",
      "rule3": "Quando sorteado, um vencedor leva tudo",
      "rule4": "Seleção aleatória comprovadamente justa"
    }
  },
  "game": {
    "title": "Corrida Veloz",
    "subtitle": "Desvie de obstáculos, colete Moon Cheese, ative power-ups",
    "resetsIn": "Reinicia em {{days}}d",
    "startGame": "Iniciar Jogo",
    "spaceToJump": "ESPAÇO / Toque para pular (pulo duplo!)",
    "gameOver": "FIM DE JOGO",
    "score": "Pontuação",
    "moonCheese": "Moon Cheese",
    "newHighScore": "Nova Pontuação Máxima!",
    "playAgain": "Jogar Novamente",
    "shareOnX": "Compartilhar no",
    "shareText": "Acabei de fazer {{score}} pontos no jogo Bullpug Speed Run! Consegue superar minha pontuação?",
    "highScore": "Pontuação Máxima",
    "totalMoonCheese": "Total de Moon Cheese",
    "controls": "Controles",
    "weeklyLeaderboard": "Ranking",
    "noScoresYet": "Sem pontuações ainda. Seja o primeiro!",
    "resetMonday": "",
    "moonCheeses": "moon cheese",
    "shield": "Escudo",
    "magnet": "Ímã",
    "doubleScore": "2x Pontos"
  }
};

const ruTranslations = {
  "common": {
    "loading": "Загрузка...",
    "error": "Произошла ошибка",
    "success": "Успешно",
    "cancel": "Отмена",
    "submit": "Отправить",
    "save": "Сохранить",
    "delete": "Удалить",
    "back": "Назад",
    "refresh": "Обновить",
    "connectWallet": "Подключить кошелек",
    "walletRequired": "Требуется кошелек",
    "language": "Язык"
  },
  "nav": {
    "home": "Главная",
    "lore": "Истоки",
    "arena": "P2P Арена",
    "game": "Космический бегун",
    "exitSim": "Выход",
    "reflections": "Размышления",
    "journal": "Журнал",
    "forum": "Форум",
    "wallet": "Кошелек",
    "admin": "Админ",
    "messages": "Сообщения"
  },
  "betting": {
    "title": "P2P Арена",
    "subtitle": "Игрок против игрока • Реальные SOL • {{rake}}% комиссия",
    "solOnly": "Только SOL",
    "disclaimer": "Для развлечения - проверьте местное законодательство",
    "connectWalletToPlay": "Подключите кошелек Solana для игры",
    "p2pRequiresWallet": "P2P ставки требуют подключенного кошелька с SOL",
    "coinFlip": {
      "title": "P2P Монетка",
      "createChallenge": "Создать вызов",
      "yourName": "Ваше имя",
      "betAmount": "Сумма ставки (SOL)",
      "yourPick": "Ваш выбор",
      "heads": "Орёл",
      "tails": "Решка",
      "opponentBet": "Ставка оппонента",
      "totalPot": "Общий банк",
      "rake": "Комиссия",
      "winnerGets": "Победитель получит",
      "creating": "Создание...",
      "createBtn": "Создать вызов",
      "yourOpenChallenges": "Ваши открытые вызовы",
      "waitingForOpponent": "Ожидание оппонента...",
      "cancel": "Отменить",
      "openChallenges": "Открытые вызовы",
      "noChallenges": "Нет открытых вызовов",
      "createOrWait": "Создайте свой или подождите!",
      "picked": "Выбрал",
      "youPick": "Вы выбираете",
      "accept": "Принять",
      "flipping": "Бросаем...",
      "recentFlips": "Недавние P2P броски",
      "youWon": "ВЫ ВЫИГРАЛИ {{amount}} SOL!",
      "youLost": "ВЫ ПРОИГРАЛИ",
      "coinLandedOn": "Выпало",
      "provablyFair": "Проверяемая честность",
      "serverSeed": "Серверное зерно",
      "clientSeed": "Клиентское зерно",
      "resultHash": "Хеш результата"
    },
    "pot": {
      "title": "Банк победителя",
      "joinPot": "Войти в банк",
      "yourName": "Ваше имя",
      "betAmount": "Сумма ставки (SOL)",
      "higherBetHigherChance": "Больше ставка = выше шанс. Победитель забирает всё минус {{rake}}% комиссия.",
      "joining": "Вход...",
      "joinBtn": "Войти в банк",
      "rakeGoesTo": "Комиссия идёт",
      "currentPot": "Текущий банк",
      "entries": "Участники",
      "won": "{{name}} выиграл {{amount}} SOL!",
      "participants": "Участники",
      "chance": "шанс",
      "noEntries": "Пока нет участников. Будьте первым!",
      "howItWorks": "Как работает P2P банк",
      "rule1": "Несколько игроков вносят SOL в банк",
      "rule2": "Ваш шанс = ваша ставка / общий банк",
      "rule3": "При розыгрыше один победитель забирает всё",
      "rule4": "Проверяемо честный случайный выбор"
    }
  },
  "game": {
    "title": "Спидран",
    "subtitle": "Уклоняйтесь от препятствий, собирайте Moon Cheese, активируйте бонусы",
    "resetsIn": "Сброс через {{days}} дн.",
    "startGame": "Начать игру",
    "spaceToJump": "ПРОБЕЛ / Тап для прыжка (двойной прыжок!)",
    "gameOver": "ИГРА ОКОНЧЕНА",
    "score": "Счёт",
    "moonCheese": "Moon Cheese",
    "newHighScore": "Новый рекорд!",
    "playAgain": "Играть снова",
    "shareOnX": "Поделиться в",
    "shareText": "Я набрал {{score}} очков в игре Bullpug Speed Run! Сможешь побить мой счёт?",
    "highScore": "Рекорд",
    "totalMoonCheese": "Всего Moon Cheese",
    "controls": "Управление",
    "weeklyLeaderboard": "Таблица лидеров",
    "noScoresYet": "Пока нет результатов. Будьте первым!",
    "resetMonday": "",
    "moonCheeses": "moon cheese",
    "shield": "Щит",
    "magnet": "Магнит",
    "doubleScore": "2× Счёт"
  }
};

const arTranslations = {
  "common": {
    "loading": "جارٍ التحميل...",
    "error": "حدث خطأ",
    "success": "نجح",
    "cancel": "إلغاء",
    "submit": "إرسال",
    "save": "حفظ",
    "delete": "حذف",
    "back": "رجوع",
    "refresh": "تحديث",
    "connectWallet": "ربط المحفظة",
    "walletRequired": "المحفظة مطلوبة",
    "language": "اللغة"
  },
  "nav": {
    "home": "الرئيسية",
    "lore": "الأصول",
    "arena": "ساحة P2P",
    "game": "العدّاء الكوني",
    "exitSim": "الخروج",
    "reflections": "تأملات",
    "journal": "المذكرات",
    "forum": "المنتدى",
    "wallet": "المحفظة",
    "admin": "الإدارة",
    "messages": "الرسائل"
  },
  "betting": {
    "title": "ساحة P2P",
    "subtitle": "لاعب ضد لاعب • SOL حقيقي • عمولة {{rake}}%",
    "solOnly": "SOL فقط",
    "disclaimer": "للترفيه - تحقق من القوانين المحلية",
    "connectWalletToPlay": "اربط محفظة Solana للعب",
    "p2pRequiresWallet": "المراهنة P2P تتطلب محفظة متصلة بها SOL",
    "coinFlip": {
      "title": "قلب العملة P2P",
      "createChallenge": "إنشاء تحدي",
      "yourName": "اسمك",
      "betAmount": "مبلغ الرهان (SOL)",
      "yourPick": "اختيارك",
      "heads": "Heads",
      "tails": "Tails",
      "opponentBet": "رهان الخصم",
      "totalPot": "المجموع الكلي",
      "rake": "العمولة",
      "winnerGets": "الفائز يحصل",
      "creating": "جارٍ الإنشاء...",
      "createBtn": "إنشاء تحدي",
      "yourOpenChallenges": "تحدياتك المفتوحة",
      "waitingForOpponent": "بانتظار الخصم...",
      "cancel": "إلغاء",
      "openChallenges": "التحديات المفتوحة",
      "noChallenges": "لا توجد تحديات مفتوحة",
      "createOrWait": "أنشئ واحدة أو انتظر الآخرين!",
      "picked": "اختار",
      "youPick": "أنت تختار",
      "accept": "قبول",
      "flipping": "جارٍ القلب...",
      "recentFlips": "قلبات P2P الأخيرة",
      "youWon": "ربحت {{amount}} SOL!",
      "youLost": "خسرت",
      "coinLandedOn": "استقرت العملة على",
      "provablyFair": "التحقق العادل القابل للإثبات",
      "serverSeed": "بذرة الخادم",
      "clientSeed": "بذرة العميل",
      "resultHash": "تجزئة النتيجة"
    },
    "pot": {
      "title": "وعاء الفائز",
      "joinPot": "الانضمام للوعاء",
      "yourName": "اسمك",
      "betAmount": "مبلغ الرهان (SOL)",
      "higherBetHigherChance": "رهان أعلى = احتمال فوز أكبر. الفائز يأخذ الكل ناقص عمولة {{rake}}%.",
      "joining": "جارٍ الانضمام...",
      "joinBtn": "الانضمام للوعاء",
      "rakeGoesTo": "العمولة تذهب إلى",
      "currentPot": "الوعاء الحالي",
      "entries": "المشاركات",
      "won": "{{name}} ربح {{amount}} SOL!",
      "participants": "المشاركون",
      "chance": "فرصة",
      "noEntries": "لا توجد مشاركات بعد. كن الأول!",
      "howItWorks": "كيف يعمل وعاء P2P",
      "rule1": "لاعبون متعددون يساهمون بـ SOL في الوعاء",
      "rule2": "احتمال فوزك = رهانك / المجموع الكلي",
      "rule3": "عند السحب، فائز واحد يأخذ الكل",
      "rule4": "اختيار عشوائي عادل قابل للإثبات"
    }
  },
  "game": {
    "title": "سباق السرعة",
    "subtitle": "تفادى العوائق، اجمع Moon Cheese، فعّل القوى الخاصة",
    "resetsIn": "يُعاد الضبط خلال {{days}} يوم",
    "startGame": "ابدأ اللعبة",
    "spaceToJump": "مسافة / اضغط للقفز (قفزة مزدوجة!)",
    "gameOver": "انتهت اللعبة",
    "score": "النقاط",
    "moonCheese": "Moon Cheese",
    "newHighScore": "رقم قياسي جديد!",
    "playAgain": "العب مجدداً",
    "shareOnX": "شارك على",
    "shareText": "سجلت للتو {{score}} نقطة في لعبة Bullpug Speed Run! هل تستطيع التغلب على نقاطي؟",
    "highScore": "أعلى نقاط",
    "totalMoonCheese": "Moon Cheese الكلي",
    "controls": "التحكم",
    "weeklyLeaderboard": "لوحة المتصدرين",
    "noScoresYet": "لا توجد نقاط بعد. كن الأول!",
    "resetMonday": "",
    "moonCheeses": "moon cheese",
    "shield": "الدرع",
    "magnet": "المغناطيس",
    "doubleScore": "نقاط × 2"
  }
};

i18n
  // LanguageDetector removed pre-launch — see PRD i165. Re-add when shipping
  // the proper multilingual refactor.
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: enTranslations },
      es: { translation: esTranslations },
      zh: { translation: zhTranslations },
      ja: { translation: jaTranslations },
      ko: { translation: koTranslations },
      de: { translation: deTranslations },
      fr: { translation: frTranslations },
      pt: { translation: ptTranslations },
      ru: { translation: ruTranslations },
      ar: { translation: arTranslations },
    },
    lng: 'en',           // Force English pre-launch.
    fallbackLng: 'en',
    supportedLngs: ['en', 'es', 'zh', 'ja', 'ko', 'de', 'fr', 'pt', 'ru', 'ar'],
    
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'bullpugLang'
    },

    interpolation: {
      escapeValue: false
    },

    react: {
      useSuspense: false
    },

    debug: false
  });

export default i18n;
